import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  FormHelperText,
  useTheme,
  Zoom,
  Container,
  Fade
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { useCommission } from '../../hooks/useCommission';
import { FastFormData, Commission, PAYMENT_PURPOSE_OPTIONS, FastTransferRequest } from './types';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TransactionSummaryCard from '../../components/TransactionSummaryCard';
import { usePermission } from '../../hooks/usePermission';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import Balance from '../../components/balance';
import { useFastoutgoingmessagesMutation } from './services/services';
import { validateIban, validateTCKN } from '../../utils/validata-iban-tckn';
import { useGetBalanceQuery } from '../../components/services/services';

const Fast = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'error' | 'warning' | 'info' | 'success'>('info');
  const [showAlert, setShowAlert] = useState(false);
  const theme = useTheme();
  const { commission, calculateCommission } = useCommission();
  const [pendingTransfer, setPendingTransfer] = useState<any>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { hasPermission: cuzdandanFASTileParaTransferi } = usePermission({
    code: "Corporate.POST.Reading.CüzdandanFASTileParaTransferi"
  });
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const { data: getBalance, } = useGetBalanceQuery(currentUserState?.companyId, { skip: !currentUserState?.companyId })

    const [isModalContentLoading, setIsModalContentLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<FastFormData>({
    defaultValues: {
      receiverIBAN: 'TR',
      receiverIdNo: '',
      receiverNameSurname: '',
      paymentPurpose: '',
      value: '',
      description: '',
      currency: '1'
    }
  });
  const [fastOutgoingMessages, { isLoading: isTransferLoading }] = useFastoutgoingmessagesMutation();
  // IBAN validation


  // Format money input
  const formatMoneyInput = (value: string) => {
    let inputValue = value.replace(/\D/g, '');
    const formatter = new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    });
    return formatter.format(parseInt(inputValue) / 100);
  };

  // Format IBAN input
  const formatIban = (value: string) => {
    let inputValue = value.toUpperCase();
    if (inputValue.startsWith('TR', 2)) {
      inputValue = inputValue.replace('TR', '');
    }
    const data = inputValue.replace(/\s/g, '');
    return 'TR' + data.substring(2, 26);
  };

  // Format money display
  const formatMoney = (value: number): string => {
    const formatter = new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    });
    return formatter.format(value);
  };

  const onSubmit = async (data: FastFormData) => {
    setIsLoading(true);

    try {
      // Parse the formatted value
      let formattedValue = data.value
        .replace('₺', '')
        .replaceAll('.', '')
        .replace(',', '.');
      const numericValue = parseFloat(formattedValue);

      if (numericValue < 1) {
        enqueueSnackbar('Tutar 1,00 ve üzeri olmalıdır', { variant: 'error' });
        return;
      }

      // Calculate commission
      await calculateCommission({
        value: numericValue,
        currencyId: data.currency,
        type: '8', // FAST type
        companyId: currentUserState.companyId,
        customerId: currentUserState.customerId
      });
      setPendingTransfer(data);
      setConfirmDialogOpen(true);
      // setConfirmDialogOpen(true);
    } catch (error) {
      enqueueSnackbar('Komisyon hesaplanırken hata oluştu', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmTransfer = async () => {
    const formData = watch();



    try {
      // Parse the formatted value
      let formattedValue = formData.value
        .replace('₺', '')
        .replaceAll('.', '')
        .replace(',', '.');
      const numericValue = parseFloat(formattedValue);
      // Bakiye kontrolü
      

      if (getBalance?.data && getBalance?.data?.totalBalance < numericValue) {
        enqueueSnackbar('Bakiyeniz ödenecek tutarın altındadır.', { variant: 'error' });
        return;
      }

      const transferRequest: FastTransferRequest = {
        currencyId: 1, // TL
        value: numericValue,
        description: formData.description || null,
        valueFee: commission.valueFee,
        receiverIBAN: formData.receiverIBAN,
        receiverNameSurname: formData.receiverNameSurname,
        receiverIdNo: formData.receiverIdNo,
        paymentPurpose: formData.paymentPurpose,
        companyId: currentUserState.companyId,
        customerId: currentUserState.customerId,
      };

      const result = await fastOutgoingMessages(transferRequest);

      setShowConfirmModal(false);

      if (result?.data?.success) {
        setPendingTransfer(null);
        setConfirmDialogOpen(false);
        reset({
          receiverIBAN: 'TR',
          receiverIdNo: '',
          receiverNameSurname: '',
          paymentPurpose: '',
          value: '',
          description: '',
          currency: '1'
        });

        enqueueSnackbar(result?.data?.message || 'Fast transferi başarıyla tamamlandı', { variant: 'success' });
      } else {
        setAlertType('error');
        setAlertMessage(result?.data?.message || 'Transfer işlemi sırasında bir hata oluştu.');
        setShowAlert(true);
        enqueueSnackbar(result?.data?.message || 'Fast transferi başarısız', { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Fast transferi sırasında bir hata oluştu', { variant: 'error' });
    }
  };
  const currencyToForm = (currencyId: number): string => {
    switch (currencyId) {
      case 2: return 'USD';
      case 3: return 'EUR';
      case 4: return 'AU';
      default: return 'TL';
    }
  };

  return (

    <Container maxWidth={false} sx={{
      flexGrow: 1,
      padding: {
        xs: '80px 8px 16px 8px',
        sm: '90px 12px 24px 12px',
        md: '110px 32px 40px 32px',
        lg: '110px 40px 40px 40px'
      },
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      minHeight: '100vh'
    }}>
      <Fade in={true} timeout={600}>
        {/* Main Dashboard Container */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 25%, #3b82f6 50%, #2a5298 75%, #1e3c72 100%)',
            }
          }}
        >
          <Box sx={{
            p: {
              xs: 1.5,
              sm: 2.5,
              md: 4
            },
            pb: {
              xs: 1,
              md: 2
            },
            background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.03) 0%, rgba(42, 82, 152, 0.06) 100%)',
            borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
          }}>

            <Box sx={{ 
              py: { xs: 1.5, sm: 2, md: 2.5, lg: 2.5 },
              px: 0
            }}>
              {/* Balance Component */}
              <Balance />

              {cuzdandanFASTileParaTransferi && <>
                <Paper
                  elevation={0}
                  sx={{
                    mt: {
                      xs: 2,
                      sm: 4,
                      md: 10
                    },
                    maxWidth: "1200px",
                    marginX: "auto",
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {/* Header */}
                  <Box sx={{
                    p: {
                      xs: 2,
                      sm: 2.5,
                      md: 3
                    },
                    borderRadius: '10px 10px 0 0',
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper'
                  }}>
                     <Typography variant="h5" component="h4" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '18px' }}>
                       FAST ile Para Gönder
                     </Typography>
                  </Box>

                  {/* FAST Information Banner */}
                  <Box sx={{
                    p: {
                      xs: 2,
                      sm: 2.5,
                      md: 3
                    },
                    backgroundColor: '#e3f2fd',
                    borderLeft: 4,
                    borderLeftColor: '#1976d2',
                    margin: {
                      xs: 2,
                      sm: 2.5,
                      md: 3
                    },
                    borderRadius: 2
                  }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1976d2',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '16px'
                      }}
                    >
                      ℹ️ FAST Bilgilendirme
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#1565c0',
                        lineHeight: 1.6
                      }}
                    >
                      {new Intl.NumberFormat('tr-TR').format(currentUserState?.fastMaxVal ?? Number(100000))} TL'ye kadar olan transfer işlemlerinizi FAST ile 7 gün 24 saat, anında yapabilirsiniz.
                    </Typography>
                  </Box>

                  {/* Alert */}
                  {showAlert && cuzdandanFASTileParaTransferi && (
                    <Box sx={{ 
                      px: {
                        xs: 2,
                        md: 3
                      }, 
                      pb: {
                        xs: 1.5,
                        md: 2
                      }
                    }}>
                      <Alert
                        severity={alertType}
                        onClose={() => setShowAlert(false)}
                        sx={{ borderRadius: 2 }}
                      >
                        {alertMessage}
                      </Alert>
                    </Box>
                  )}

                  {/* Form */}
                  {cuzdandanFASTileParaTransferi && <Box sx={{ 
                    p: {
                      xs: 2,
                      sm: 3,
                      md: 4
                    }
                  }}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Grid container spacing={{
                        xs: 2,
                        sm: 2.5,
                        md: 3
                      }}>
                        {/* First Row: IBAN (Full Width) */}
                        <Grid item xs={12}>
                          <Controller
                            name="receiverIBAN"
                            control={control}
                            rules={{
                              required: 'IBAN gereklidir',
                              minLength: { value: 26, message: '26 haneli IBAN giriniz' },
                              validate: (value) => validateIban(value) || 'Geçersiz IBAN'
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="IBAN *"
                                fullWidth
                                placeholder="IBAN Giriniz"
                                error={!!errors.receiverIBAN}
                                helperText={errors.receiverIBAN?.message}
                                onChange={(e) => {
                                  const formatted = formatIban(e.target.value);
                                  field.onChange(formatted);
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    minHeight: '56px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Second Row: TCKN + Receiver Name (2 columns on desktop, 1 on mobile) */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="receiverIdNo"
                            control={control}
                            rules={{
                              validate: (value) =>
                                !value || value.length === 11
                                  ? (!value || validateTCKN(value) || 'Geçersiz TCKN')
                                  : 'TCKN 11 karakter olmalıdır'
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="TCKN"
                                fullWidth
                                placeholder="TCKN Giriniz"
                                type="number"
                                error={!!errors.receiverIdNo}
                                helperText={errors.receiverIdNo?.message}
                                onChange={(e) => {
                                  const value = e.target.value.slice(0, 11); // Limit to 11 characters
                                  field.onChange(value);
                                }}
                                inputProps={{ maxLength: 11 }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    minHeight: '56px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    }
                                  },
                                  '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                  },
                                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="receiverNameSurname"
                            control={control}
                            rules={{ required: 'Alıcı Ad Soyad gereklidir' }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Alıcı Ad Soyad *"
                                fullWidth
                                placeholder="Alıcı Ad Soyad"
                                error={!!errors.receiverNameSurname}
                                helperText={errors.receiverNameSurname?.message}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    minHeight: '56px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Third Row: Payment Purpose + Amount (2 columns on desktop, 1 on mobile) */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="paymentPurpose"
                            control={control}
                            rules={{ required: 'Ödeme nedeni seçiniz' }}
                            render={({ field }) => (
                              <FormControl fullWidth error={!!errors.paymentPurpose}>
                                <InputLabel>Ödeme Nedeni *</InputLabel>
                                <Select
                                  {...field}
                                  label="Ödeme Nedeni *"
                                  sx={{
                                    borderRadius: 2,
                                    minHeight: '56px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    }
                                  }}
                                >
                                  {PAYMENT_PURPOSE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {errors.paymentPurpose && (
                                  <FormHelperText>{errors.paymentPurpose.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="value"
                            control={control}
                            rules={{
                              required: 'Tutar gereklidir',
                              validate: (value) => {
                                const numericValue = parseFloat(
                                  value.replace('₺', '').replaceAll('.', '').replace(',', '.')
                                );
                                return numericValue >= 1 || 'Tutar 1,00 ve üzeri olmalıdır';
                              }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Tutar *"
                                fullWidth
                                placeholder="Tutar Giriniz"
                                error={!!errors.value}
                                helperText={errors.value?.message}
                                onChange={(e) => {
                                  const formatted = formatMoneyInput(e.target.value);
                                  field.onChange(formatted);
                                }}
                                onKeyDown={(e) => {
                                  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
                                  if (!allowedKeys.includes(e.key) && (e.key < '0' || e.key > '9')) {
                                    e.preventDefault();
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    minHeight: '56px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Fourth Row: Description (Full Width) */}
                        <Grid item xs={12}>
                          <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Açıklama"
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Açıklama"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#1976d2',
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                            <Button
                              type="submit"
                              variant="contained"
                              size="large"
                              fullWidth
                              endIcon={<ArrowForwardIcon />}
                              sx={{
                                borderRadius: 3,
                                py: 1.5,
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 12px 32px ${theme.palette.primary.main}50`,
                                },
                                transition: 'all 0.3s ease-in-out'
                              }}
                            >
                              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'FAST ile Gönder'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </Box>}
                </Paper>
                <ConfirmationModal
                  isOpen={confirmDialogOpen}
                  onClose={() => setConfirmDialogOpen(false)}
                  isLoading={isTransferLoading}
                  onConfirm={handleConfirmTransfer}
                  confirmButtonDisabled={isTransferLoading || isModalContentLoading}
                  title="Fast Transfer Onayı"
                  confirmButtonText="Gönder"
                  closeButtonText="İptal"
                  optionalWidth="800px !important"
                >
                  {pendingTransfer && (
                    <TransactionSummaryCard
                      firstFieldName="Alıcı IBAN"
                      walletNo={pendingTransfer?.receiverIBAN || ''}
                      value={pendingTransfer?.value}
                      commissionValue={commission?.valueFee || 0}
                      totalValue={commission?.total || 0}
                      currencyLabel={currencyToForm(Number(pendingTransfer?.currency))}
                      formatMoney={formatMoney}
                    />
                  )}
                </ConfirmationModal>
              </>}
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Container>
  );
};

export default Fast;